import React, { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { authAPI, type LoginUser } from "@/lib/api";
import type { ModulePermissionEntry } from "@/lib/modulePermissions";
import { applyFailedLogin, loginRetryAfterSeconds, loginWaitText } from "./loginErrors";
import { MfaEnrollWizard } from "./MfaEnrollWizard";
import { MfaChallenge } from "./MfaChallenge";

interface LoginPageProps {
  onLogin: (user: any, token: string) => void;
}

export interface UserData {
  id: number;
  codigo: string;
  username: string;
  email: string;
  nombre: string;
  apellido: string;
  rol: string;
  /** API `is_admin` (camelCase). Staff/superuser. El nombre del rol no otorga. */
  isAdmin?: boolean;
  is_admin?: boolean;
  isStaff?: boolean;
  is_staff?: boolean;
  isSuperuser?: boolean;
  is_superuser?: boolean;
  /** Derived: is_superuser OR rol.canonico. Login /me/; not Django is_superuser. */
  isElevated?: boolean;
  is_elevated?: boolean;
  bloquearPersonalizadosExistentes?: boolean;
  bloquear_personalizados_existentes?: boolean;
  modulosPermitidos: number[];
  modulePermissions: ModulePermissionEntry[];
  puntosVenta?: any[];
  puntoVentaDefault?: string | null;
  sessionDuration?: number;
  session_duration?: number;
  punto_venta_default?: string | null;
  avatar_url?: string | null;
  avatarUrl?: string | null;
}

function loginBackgroundClass(): string {
  const host = typeof window === "undefined" ? "" : window.location.hostname;
  if (host === "dev.sistemaaba.com.ar") {
    return "bg-gradient-to-br from-[#4a2428] to-[#6e3a3a]";
  }
  if (host === "preview.sistemaaba.com.ar") {
    return "bg-gradient-to-br from-[#4a321c] to-[#7a5428]";
  }
  return "bg-gradient-to-br from-[#1a2744] to-[#2d4a7c]";
}

export function LoginPage({ onLogin }: LoginPageProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [retryIn, setRetryIn] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [enrollToken, setEnrollToken] = useState<string | null>(null);
  const [mfaToken, setMfaToken] = useState<string | null>(null);

  const enterApp = (user: LoginUser, token: string) => {
    toast.success(`Bienvenido ${user.nombre}`);
    onLogin(user, token);
  };

  useEffect(() => {
    if (retryIn <= 0) return;
    const id = window.setTimeout(() => setRetryIn((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [retryIn]);

  const resetToCredentials = () => {
    setEnrollToken(null);
    setMfaToken(null);
    setPassword("");
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!username || !password) {
      setError("Por favor complete todos los campos");
      return;
    }

    setIsLoading(true);

    try {
      const response = await authAPI.login({ username, password });

      if (response.kind === "enroll") {
        setPassword("");
        setEnrollToken(response.enrollToken);
        return;
      }
      if (response.kind === "mfa") {
        setPassword("");
        setMfaToken(response.mfaToken);
        return;
      }

      toast.success(`Bienvenido ${response.user.nombre}`);
      onLogin(response.user, response.token);
    } catch (err: unknown) {
      console.error("Login error:", err);
      const failed = applyFailedLogin(err);
      const wait = loginRetryAfterSeconds(err);
      setUsername(failed.username);
      setPassword(failed.password);
      setRetryIn(wait);
      setError(wait > 0 ? loginWaitText(wait) : failed.error);
    } finally {
      setIsLoading(false);
    }
  };

  const title =
    enrollToken != null
      ? "Activar MFA"
      : mfaToken != null
        ? "Verificación MFA"
        : "Iniciar Sesión";
  const subtitle =
    enrollToken != null
      ? "Enrolá tu authenticator para continuar"
      : mfaToken != null
        ? "Ingresá el código de tu authenticator"
        : "Ingrese sus credenciales para continuar";

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center ${loginBackgroundClass()}`}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="flex flex-col items-center mb-8">
            <img
              src="/logotipo-color-no-bg-no-slogan.png"
              alt="ABS"
              width={154}
              height={90}
              className="w-[154px] h-[90px] object-contain mb-4"
            />
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            <p className="text-sm text-gray-600 mt-2">{subtitle}</p>
          </div>

          {enrollToken ? (
            <MfaEnrollWizard
              enrollToken={enrollToken}
              onComplete={enterApp}
              onBack={resetToCredentials}
            />
          ) : mfaToken ? (
            <MfaChallenge
              mfaToken={mfaToken}
              onComplete={enterApp}
              onBack={resetToCredentials}
            />
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Usuario
                </label>
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d97706] focus:border-transparent text-gray-900"
                  placeholder="Ingrese su usuario"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contraseña
                </label>
                <input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d97706] focus:border-transparent text-gray-900"
                  placeholder="Ingrese su contraseña"
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle />
                  <AlertTitle>No se pudo iniciar sesión</AlertTitle>
                  <AlertDescription>
                    {retryIn > 0
                      ? loginWaitText(retryIn)
                      : error.startsWith("Demasiados intentos")
                        ? loginWaitText(0)
                        : error}
                  </AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                disabled={isLoading || retryIn > 0}
                className="w-full bg-[#d97706] hover:bg-[#b86607] text-white"
              >
                {isLoading ? "Ingresando..." : "Ingresar"}
              </Button>
            </form>
          )}
        </div>
        <p className="mt-6 text-center text-xs text-white/70">
          © 2026 ABA | Abertura Buenos Aires - Todos los derechos reservados.
        </p>
      </div>
    </div>
  );
}
